import { z } from 'zod';

export function createPaginatedRequestSchema(maxPageSize = 100) {
  return z.object({
    cursor: z.string().optional(),
    pageSize: z.coerce.number().min(1).max(maxPageSize).default(20),
  });
}

export type PaginatedRequest = z.infer<ReturnType<typeof createPaginatedRequestSchema>>;

export function createPaginatedResponseSchema<TItem extends z.ZodTypeAny>(itemSchema: TItem) {
  return z.object({
    data: z.array(itemSchema),
    meta: z.object({
      nextCursor: z.string().nullable(),
      pageSize: z.number(),
      totalCount: z.number(),
    }),
  });
}

export type PaginatedResponse<TItem> = {
  data: TItem[];
  meta: {
    nextCursor: string | null;
    pageSize: number;
    totalCount: number;
  };
};

/**
 * Shared offset-based cursor codec for the offset-encoded pagination idiom
 * (media-units + offer-units). The cursor is the next row offset encoded as a
 * decimal string; `null`/absent means "from the start".
 */

/** Decode an offset cursor into a row offset. Absent cursor → 0. */
export function decodeOffsetCursor(cursor?: string): number {
  return cursor != null ? Number(cursor) : 0;
}

/**
 * Encode the next-page cursor. Returns the next offset as a string only when the
 * current page was full (a further page may exist); otherwise `null` (last page).
 */
export function encodeOffsetCursor(
  offset: number,
  rowCount: number,
  pageSize: number,
): string | null {
  return rowCount === pageSize ? String(offset + rowCount) : null;
}

/**
 * Slices a full, already-fetched row array into one offset-paginated page.
 * Shared by services that fetch all rows and paginate in memory (offer
 * pricing, offer availability) rather than pushing LIMIT/OFFSET to the DB.
 */
export function sliceOffsetPage<T>(
  rows: T[],
  cursor: string | undefined,
  pageSize: number,
): PaginatedResponse<T> {
  const offset = decodeOffsetCursor(cursor);
  const data = rows.slice(offset, offset + pageSize);
  return {
    data,
    meta: {
      nextCursor: encodeOffsetCursor(offset, data.length, pageSize),
      pageSize,
      totalCount: rows.length,
    },
  };
}
